import { baseApi, normalizeResourceId, resourceScopeTag } from '@krp/client-runtime'

import { applyPair, predictPage } from '../model/optimistic'
import {
  PAGE_SIZE,
  type DecisionArgs,
  type DecisionResult,
  type DuplicateSource,
  type PairsArgs,
  type PairsPage,
  type ScanStatus,
} from '../model/types'
import { decodeDecisionResult, decodePairsPage, decodeScanStatus, decodeSources } from './contracts'
import { DUPLICATES_URLS } from './urls'

/**
 * Служба дублей в общем `baseApi`. Команды не повторяются transport-ом (ADR-11): каждая несёт `operationId` намерения человека,
 * повтор тем же ключом служба узнаёт сама (Р-1). Данные сервера живут только в этом кэше (И-14).
 */
export const DUPLICATES_TAG = 'Duplicates' as const

const SOURCES = { type: DUPLICATES_TAG, id: 'SOURCES' } as const
const scanTag = (resourceId: string) => ({ type: DUPLICATES_TAG, id: `SCAN:${normalizeResourceId(resourceId)}` }) as const
const pairsTag = (resourceId: string) => ({ type: DUPLICATES_TAG, id: `PAIRS:${normalizeResourceId(resourceId)}` }) as const

export interface ResourceArgs {
  resourceId: string
}

export interface ScanStartArgs extends ResourceArgs {
  threshold: number
  operationId: string
}

export interface ScanControlArgs extends ResourceArgs {
  operationId: string
}

export type DecisionCommand = DecisionArgs & ResourceArgs

function decisionUrl({ resourceId, pairId, kind }: DecisionCommand): string {
  switch (kind) {
    case 'copy':
      return DUPLICATES_URLS.pairCopy(resourceId, pairId)
    case 'not-duplicate':
      return DUPLICATES_URLS.pairNotDuplicate(resourceId, pairId)
    case 'reset':
      return DUPLICATES_URLS.pairReset(resourceId, pairId)
  }
}

export const duplicatesApi = baseApi.enhanceEndpoints({ addTagTypes: [DUPLICATES_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    duplicateSources: build.query<DuplicateSource[], void>({
      query: () => ({ url: DUPLICATES_URLS.sources, decode: decodeSources }),
      providesTags: [SOURCES],
    }),
    duplicateScan: build.query<ScanStatus, ResourceArgs>({
      query: ({ resourceId }) => ({ url: DUPLICATES_URLS.scan(resourceId), decode: decodeScanStatus }),
      providesTags: (_result, _error, { resourceId }) => [scanTag(resourceId), resourceScopeTag(resourceId)],
    }),
    duplicatePairs: build.query<PairsPage, PairsArgs>({
      query: ({ resourceId, filter, page }) => ({
        url: DUPLICATES_URLS.pairs(resourceId),
        params: { state: filter, page: String(page), pageSize: String(PAGE_SIZE) },
        decode: decodePairsPage,
      }),
      providesTags: (_result, _error, { resourceId }) => [pairsTag(resourceId), resourceScopeTag(resourceId)],
    }),
    // Тело ответа запуска, остановки и продолжения клиенту не нужно: договор описывает только `GET …/scan`, а после команды
    // состояние перечитывается им же (теги ниже). Так клиент не зависит от формы ответа, которой в договоре нет.
    duplicateScanStart: build.mutation<unknown, ScanStartArgs>({
      query: ({ resourceId, threshold, operationId }) => ({
        url: DUPLICATES_URLS.scan(resourceId),
        method: 'POST',
        body: { threshold, operationId },
        idempotent: true,
      }),
      invalidatesTags: (_result, error, { resourceId }) => (error ? [] : [scanTag(resourceId), pairsTag(resourceId), SOURCES]),
    }),
    duplicateScanStop: build.mutation<unknown, ScanControlArgs>({
      query: ({ resourceId, operationId }) => ({
        url: DUPLICATES_URLS.scanStop(resourceId),
        method: 'POST',
        body: { operationId },
        idempotent: true,
      }),
      invalidatesTags: (_result, error, { resourceId }) => (error ? [] : [scanTag(resourceId), SOURCES]),
    }),
    duplicateScanResume: build.mutation<unknown, ScanControlArgs>({
      query: ({ resourceId, operationId }) => ({
        url: DUPLICATES_URLS.scanResume(resourceId),
        method: 'POST',
        body: { operationId },
        idempotent: true,
      }),
      invalidatesTags: (_result, error, { resourceId }) => (error ? [] : [scanTag(resourceId), SOURCES]),
    }),
    /**
     * «Это копия», «Не дубли», «Снять отметку». Страница списка на экране правится сразу, до ответа (И-12); отказ откатывает её,
     * а текст отказа остаётся в результате команды — экран показывает его у пары. Ответ службы заменяет догадку: пара и счётчики
     * берутся из него.
     */
    duplicateDecide: build.mutation<DecisionResult, DecisionCommand>({
      query: (command) => ({
        url: decisionUrl(command),
        method: 'POST',
        body: command.kind === 'copy' ? { key: command.key, operationId: command.operationId } : { operationId: command.operationId },
        idempotent: true,
        decode: decodeDecisionResult,
      }),
      onQueryStarted: async (command, { dispatch, queryFulfilled }): Promise<void> => {
        const patch = dispatch(
          duplicatesApi.util.updateQueryData('duplicatePairs', command.view, (draft) => {
            const predicted = predictPage(draft, command.pairId, command.kind, command.key)
            if (predicted !== null) Object.assign(draft, predicted)
          }),
        )
        try {
          const { data } = await queryFulfilled
          dispatch(duplicatesApi.util.updateQueryData('duplicatePairs', command.view, (draft) => applyPair(draft, data.pair, data.counters)))
        } catch {
          // Отказ показывает результат команды у пары; страница возвращается к тому, что было до нажатия.
          patch.undo()
        }
      },
      invalidatesTags: (_result, error, { resourceId }) => (error ? [] : [pairsTag(resourceId), SOURCES]),
    }),
  }),
})

export const {
  duplicateSources,
  duplicateScan,
  duplicatePairs,
  duplicateScanStart,
  duplicateScanStop,
  duplicateScanResume,
  duplicateDecide,
} = duplicatesApi.endpoints
